import React, { useState } from 'react';
import { StyleSheet, View, TouchableOpacity, Text } from 'react-native';
import { MessageInput, MessageList, useChannelActionContext, useChannelStateContext } from "stream-chat-react";
import { FontAwesome } from "@expo/vector-icons";
import { ChannelHeader } from "./ChannelHeader";
import { useChannelData } from "@/hooks/useChannelData";
import { deleteChannel } from "@/services/universe/client";
import { useRouter } from "expo-router";
import { UniverseStreamChannel } from '@/services/stream/types';
import { getStatusPermissions } from '@/utils/statusPermissions';

export function ChannelContent({ isTablet }: { isTablet?: boolean } = {}) {
    const { channel } = useChannelStateContext();
    const { canDelete, status } = useChannelData(channel as UniverseStreamChannel);
    const { canSendMessage, canSendMedia, blockedInputPlaceholder } = getStatusPermissions(status);
    const router = useRouter();

    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        const channelId = channel?.id;
        if (!channelId || deleting) return;

        if (window.confirm("Are you sure you want to delete this channel?")) {
            setDeleting(true);
            try {
                await deleteChannel(channelId);
                router.back();
            } catch (error) {
                console.error("Error deleting channel:", error);
                window.alert("Failed to delete the channel. Please try again.");
            } finally {
                setDeleting(false);
            }
        }
    };

    return (
        <View style={[styles.container, { backgroundColor: 'transparent' }]}>
            <ChannelHeader channel={channel as UniverseStreamChannel} isTablet={isTablet} />
            <MessageList />
            <View style={[styles.inputContainer, { paddingBottom: 10 }]}>
                {canDelete ? (
                    <View style={styles.deleteButtonContainer}>
                        <TouchableOpacity
                            style={[styles.deleteButton, deleting && { opacity: 0.5 }]}
                            onPress={handleDelete}
                            disabled={deleting}
                        >
                            <FontAwesome name="trash" size={18} color="#e11d48" />
                            <Text style={styles.deleteButtonText}>{deleting ? 'Deleting…' : 'Delete'}</Text>
                        </TouchableOpacity>
                    </View>
                ) : (
                    <MessageInput
                        additionalTextareaProps={
                            !canSendMessage
                                ? { readOnly: true, placeholder: blockedInputPlaceholder }
                                : undefined
                        }
                        audioRecordingEnabled={canSendMedia}
                        maxRows={6}
                    />
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    inputContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    deleteButtonContainer: {
        padding: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    deleteButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#e11d48',
        borderRadius: 8,
        paddingVertical: 10,
        paddingHorizontal: 20,
        width: '50%',
    },
    deleteButtonText: {
        color: '#e11d48',
        fontSize: 16,
        fontWeight: '500',
        marginLeft: 8,
    },
});
